import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { User, UserAction } from '@app/core/models';
import { Notification } from '@app/core/models/notification/notification';
import { NotificationTypes } from '@app/core/models/notification/notification-types';
import { AuthService, ModalService, NotificationsService } from '@app/core/services';
import { ToastsService } from '@app/core/services/toasts.service';
import { MessageModalComponent } from '@app/shared/message-modal.component';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { UsersService } from '../../services/users.service';
import {
  acceptEula, acceptEulaSuccess,
  createOrUpdateUser, createOrUpdateUserFailure, createOrUpdateUserSuccess,
  deleteUser, deleteUserSuccess,
  loadOrgUsers, loadOrgUsersSuccess,
  loadUser, loadUserSuccess,
  loadUsers, loadUsersSuccess,
  updateUserPreference, updateUserPreferenceFailure, updateUserPreferenceSuccess,
  updateUserSearchOption, updateUserSearchOptionFailure, updateUserSearchOptionSuccess
} from '../actions/user.actions';
import { AppState } from '../reducers';
import { UsersState, selectUsersState } from '../reducers/users.reducer';
@Injectable()
export class UserEffects {

  usersState: UsersState;

  constructor(
    protected actions: Actions,
    private store: Store<AppState>,
    private usersService: UsersService,
    private toastService: ToastsService,
    private notificationsService: NotificationsService,
    private modalService: ModalService,
    private authService: AuthService,
    private router: Router
  ) {
    this.store.select(selectUsersState).pipe(takeUntilDestroyed()).subscribe((usersState) => {
      this.usersState = usersState;
    });
  }

  loadUser = createEffect(() => {
    return this.actions.pipe(
      ofType(loadUser),
      distinctUntilChanged(),
      switchMap(() => this.usersService.loadUser()),
      map((user) => loadUserSuccess({ user: User.userFactory(user) }))
    )
  });

  loadUsers = createEffect(() => {
    return this.actions.pipe(
      ofType(loadUsers),
      distinctUntilChanged(),
      switchMap(() => this.usersService.getUsers().pipe(
        map((results) => {
          let users: User[] = [];
          results.forEach(u => users.push(User.userFactory(u)));

          return loadUsersSuccess({ users: users });
        })))
    );
  });

  loadOrgUsers = createEffect(() => {
    return this.actions.pipe(
      ofType(loadOrgUsers),
      distinctUntilChanged(),
      switchMap((action) => this.usersService.getOrgUsers(action.orgId)),
      map((orgUsers) => loadOrgUsersSuccess({ users: orgUsers }))
    )
  });

  createOrUpdateUser = createEffect(() => {
    return this.actions.pipe(
      ofType(createOrUpdateUser),
      switchMap((action) => this.usersService.createOrUpdate(action.userAction).pipe(
        map((userAction) => {
          this.store.dispatch(loadUser());
          this.router.navigate(['/portal/users']);
          const textUpdatedOrCreated = userAction.method === 'PUT' ? 'updated' : 'created';
          let user = User.userFactory(userAction.user);
          const toastMessage = `  User <strong>${user.fullName}</strong> was ${textUpdatedOrCreated}.`
          this.toastService.success(toastMessage);
          return createOrUpdateUserSuccess({ userAction: userAction });
        }),
        catchError((response) => {
          const notification = {
            target: 'add-edit-user',
            text: null,
            type: NotificationTypes.DANGER,
            dismissible: false,
            dimissAfter: 0,
            listItems: null,
          } as Notification;

          if (response.error && response.error.status === 'User Exists') {
            this.userExists(action.userAction.user);
          } else {
            notification.text = 'We were unable to complete your request. Please try again.';
            this.notificationsService.notify(notification);
          }
          return of(createOrUpdateUserFailure({ userAction: action.userAction, error: response.error }));
        })
      ),
      )
    )
  });

  userExists(userData: User): void {
    let userFound = undefined;
    if (this.usersState && this.usersState.users) {
      userFound = this.usersState.users.find(
        (u) => u.userLoginName.toLocaleLowerCase() === userData.userLoginName.toLocaleLowerCase()
      );
    }

    const modalRef = this.modalService.open(MessageModalComponent);
    if (userFound) {
      modalRef.componentInstance.title = "This user already exists."
      modalRef.componentInstance.modalText = 'Click edit button to view or edit this user&#8217;s information.';
      modalRef.componentInstance.labelConfirm = 'Edit';
    } else {
      modalRef.componentInstance.title = "This user already exists."
      modalRef.componentInstance.modalText = 'If you need to add this user to your organization, contact your managed care executive.';
      modalRef.componentInstance.labelConfirm = 'Close';
      modalRef.componentInstance.extraCssLinkBtn = 'invisible';
    }

    modalRef.result.then(result => {
      this.modalService.dismissAll();
      if (result && userFound) {
        this.router.navigate([`/portal/users/edit/${userFound.userId}`])
      };
    });
  }

  deleteUser = createEffect(() => {
    return this.actions.pipe(
      ofType(deleteUser),
      switchMap(action => {
        return this.usersService.delete(action.user.userId).pipe(
          map(() => {
            if (this.usersState.user.userId === action.user.userId) { // handle user delete themself
              this.authService.signOut();
            } else {
              this.store.dispatch(loadUsers()); //update the list
              this.router.navigate(['/portal/users']);
              const toastMessage = ` User <strong>${action.user.firstName} ${action.user.lastName}</strong> was deleted.`
              this.toastService.success(toastMessage);
            }

            return deleteUserSuccess();
          })
        )
      })
    )
  });

  updateUserPreference = createEffect(() => {
    return this.actions.pipe(
      ofType(updateUserPreference),
      switchMap((action) => this.usersService.updatePreference(action.user)),
      map((u) => {
        this.store.dispatch(loadUser());
        const toastMessage = ` Your preferences have been updated.`
        this.toastService.success(toastMessage);
        return updateUserPreferenceSuccess();
      }),
      catchError(err => {
        const modalRef = this.modalService.open(MessageModalComponent);
        modalRef.componentInstance.modalText = ' Your update User Preference could not be saved at this time.';
        modalRef.componentInstance.title = ' User Preference Error';
        modalRef.componentInstance.labelConfirm = 'Close';
        modalRef.componentInstance.extraCssLinkBtn = 'invisible';
        modalRef.componentInstance.extraCssPrimaryBtn = 'btn-primary';
        return of(updateUserPreferenceFailure({ error: err }));
      })
    )
  });

  updateUserSearchOption = createEffect(() => {
    return this.actions.pipe(
      ofType(updateUserSearchOption),
      switchMap((action) => this.usersService.createOrUpdate(action.userAction).pipe(
        map((userAction) => {
          return updateUserSearchOptionSuccess({ user: User.userFactory(userAction.user) });
        }),
        catchError(err => {
          const modalRef = this.modalService.open(MessageModalComponent);
          modalRef.componentInstance.modalText = ' Your update User Search Options could not be saved at this time.';
          modalRef.componentInstance.title = ' User Search Options Error';
          modalRef.componentInstance.labelConfirm = 'Close';
          modalRef.componentInstance.extraCssLinkBtn = 'invisible';
          modalRef.componentInstance.extraCssPrimaryBtn = 'btn-primary';

          return of(updateUserSearchOptionFailure({ userAction: action.userAction, error: err }));
        })
      )
      ))
  });

  acceptEula = createEffect(() => {
    return this.actions.pipe(
      ofType(acceptEula),
      map((o) => {
        const userAction = <UserAction><unknown>o;
        return acceptEulaSuccess({ userAction: userAction });
      })
    )
  });
}